<template>
  <Drawer
    v-model:visible="visible"
    position="right"
    header="Configure Agent"
    :dismissable="false"
    :style="{ width: '28rem' }"
  >
    <!-- Loading config from agent -->
    <div
      v-if="fetchingConfig"
      class="flex items-center justify-center gap-2.5 py-12"
    >
      <i class="pi pi-spinner pi-spin text-navy-800" />
      <span class="text-navy-700 text-sm font-medium">
        Fetching current configuration from agent...
      </span>
    </div>

    <template v-else>
      <div class="mb-6 flex items-start gap-4">
        <div
          class="flex h-10 w-10 shrink-0 items-center justify-center rounded-[10px] bg-blue-50 text-lg"
        >
          <i class="pi pi-cog" />
        </div>
        <div>
          <h2 class="text-navy-800 mb-1 text-[17px] font-semibold">
            Configure SoftPro connection
          </h2>
          <p class="text-navy-500 text-sm leading-relaxed">
            Enter your SoftPro Select server details. The agent authenticates
            using its Windows service account — no password needed.
          </p>
        </div>
      </div>

      <!-- Agent Name -->
      <div class="mb-5">
        <label class="text-navy-700 mb-1.5 block text-sm font-medium">
          Agent Name
        </label>
        <input
          v-model="agentName"
          type="text"
          placeholder="e.g. Production Agent"
          class="text-navy-800 placeholder-navy-300 w-full rounded-md border border-neutral-300 px-3 py-2 text-sm transition-colors focus:border-green-500 focus:ring-2 focus:ring-green-500/20 focus:outline-none"
        />
        <p class="text-navy-400 mt-1.5 text-sm">
          A friendly name to identify this agent
        </p>
      </div>

      <!-- Server URL -->
      <div class="mb-5">
        <label class="text-navy-700 mb-1.5 block text-sm font-medium">
          SoftPro Server URL
        </label>
        <div class="flex gap-2">
          <input
            v-model="serverUrl"
            type="text"
            placeholder="http://server:8080"
            class="text-navy-800 placeholder-navy-300 flex-1 rounded-md border border-neutral-300 px-3 py-2 font-mono text-sm transition-colors focus:border-green-500 focus:ring-2 focus:ring-green-500/20 focus:outline-none"
            @input="onServerUrlChange"
          />
          <AppButton
            variant="secondary"
            :disabled="!serverUrl.trim() || profilesLoading"
            :loading="profilesLoading"
            @click="fetchProfiles"
          >
            Fetch Profiles
          </AppButton>
        </div>
        <p class="text-navy-400 mt-1.5 text-sm">
          The URL your team uses to access SoftPro Select
        </p>
      </div>

      <!-- Profile Dropdown -->
      <div class="mb-7">
        <label class="text-navy-700 mb-1.5 block text-sm font-medium">
          Profile
        </label>
        <div
          v-if="profilesLoading"
          class="flex items-center gap-2 rounded-md border border-neutral-300 bg-neutral-50 px-3 py-2.5 text-sm text-neutral-400"
        >
          <i class="pi pi-spinner pi-spin text-sm" />
          Fetching profiles from agent...
        </div>
        <AppSelect
          v-else-if="profiles.length > 0"
          v-model="selectedProfile"
          :options="profiles"
          placeholder="Select a profile..."
        />
        <div
          v-else
          class="rounded-md border border-dashed border-neutral-300 bg-neutral-50 px-3 py-2.5 text-sm text-neutral-400"
        >
          Enter server URL and click "Fetch Profiles"
        </div>
        <p v-if="profileError" class="mt-1.5 text-sm text-red-600">
          {{ profileError }}
        </p>
      </div>

      <!-- Test Order Number (optional) -->
      <div class="mb-7">
        <label class="text-navy-700 mb-1.5 block text-sm font-medium">
          Test Order Number
          <span class="text-navy-400 font-normal">(optional)</span>
        </label>
        <input
          v-model="testOrderNumber"
          type="text"
          :disabled="!selectedProfile"
          placeholder="e.g. ORD-22-0000001"
          class="text-navy-800 placeholder-navy-300 w-full rounded-md border border-neutral-300 px-3 py-2 font-mono text-sm transition-colors focus:border-green-500 focus:ring-2 focus:ring-green-500/20 focus:outline-none disabled:cursor-not-allowed disabled:bg-neutral-100 disabled:text-neutral-400"
        />
        <p class="text-navy-400 mt-1.5 text-sm">
          Enter an order number to verify the agent can retrieve orders from
          SoftPro
        </p>
      </div>

      <!-- Submit / Testing -->
      <div v-if="submitting" class="space-y-3">
        <div
          class="flex items-center justify-center gap-2.5 rounded-md border border-neutral-200 bg-neutral-50 px-6 py-3.5"
        >
          <i class="pi pi-spinner pi-spin text-navy-800" />
          <span class="text-navy-700 text-sm font-medium">
            Testing connection...
          </span>
        </div>
        <button
          type="button"
          class="text-navy-400 hover:text-navy-600 w-full text-center text-sm transition-colors"
          @click="submitting = false"
        >
          Cancel
        </button>
      </div>
      <div v-else class="space-y-3">
        <AppButton
          class="w-full"
          :disabled="!selectedProfile || !serverUrl.trim()"
          @click="submitConfiguration"
        >
          Test &amp; Connect
        </AppButton>
        <button
          type="button"
          class="text-navy-400 hover:text-navy-600 w-full text-center text-sm transition-colors"
          @click="handleCancel"
        >
          Cancel &amp; Start Over
        </button>
      </div>
    </template>
  </Drawer>
</template>

<script setup lang="ts">
import Drawer from "primevue/drawer"
import { useToast } from "primevue/usetoast"
import { onUnmounted, ref, watch } from "vue"

import type { AgentRegistrationType } from "../../pages/Company/types"
import DISPATCH_GET_CURRENT_CONFIG from "../../queries/agent_registration/dispatchGetCurrentConfig.gql"
import DISPATCH_LIST_PROFILES from "../../queries/agent_registration/dispatchListProfiles.gql"
import REVOKE_AGENT from "../../queries/agent_registration/revokeAgent.gql"
import SUBMIT_ENCRYPTED_CREDENTIALS from "../../queries/agent_registration/submitEncryptedCredentials.gql"
import POLL_WORK_ITEM from "../../queries/agent_work_item/poll.gql"
import { apolloClient } from "../../queries/client"
import { subscribeToCompany, type AblySubscription } from "../../services/cable"
import { encryptCredentials } from "../../utils/crypto"
import AppButton from "../ui/AppButton.vue"
import AppSelect from "../ui/AppSelect.vue"

type AgentRegistrationUpdatedPayload = {
  type: string
  agent_registration_id: string
  agent_registration_gid: string
  status: string
  name?: string | null
  hostname?: string | null
  ip_address?: string | null
  agent_version?: string | null
  last_heartbeat_at?: string | null
}

interface Props {
  registration: AgentRegistrationType | null
  companyId: string
  initialServerUrl?: string
  initialProfile?: string
}

const props = defineProps<Props>()

const visible = defineModel<boolean>("visible", { default: false })

const emit = defineEmits<{
  configured: [registration: AgentRegistrationType]
  cancel: []
}>()

const toast = useToast()

// ─── Form State ─────────────────────────────────────────────────

const agentName = ref("")
const serverUrl = ref("")
const selectedProfile = ref("")
const profiles = ref<string[]>([])
const pendingProfile = ref("")
const testOrderNumber = ref("")
const profilesLoading = ref(false)
const profileError = ref("")
const submitting = ref(false)
const fetchingConfig = ref(false)

const resetForm = () => {
  agentName.value = props.registration?.name ?? ""
  serverUrl.value = props.initialServerUrl ?? ""
  selectedProfile.value = ""
  profiles.value = []
  pendingProfile.value = props.initialProfile ?? ""
  testOrderNumber.value = ""
  profilesLoading.value = false
  profileError.value = ""
  submitting.value = false
  fetchingConfig.value = false
}

const onServerUrlChange = () => {
  profiles.value = []
  selectedProfile.value = ""
  profileError.value = ""
}

// ─── WebSocket Subscription ─────────────────────────────────────

let subscription: AblySubscription | null = null

const startSubscription = async () => {
  if (subscription) return

  subscription = await subscribeToCompany<AgentRegistrationUpdatedPayload>(
    props.companyId,
    (data) => {
      if (data?.type !== "agent_registration_updated") return

      const regId = data.agent_registration_id
      if (!regId) return
      if (props.registration && regId !== props.registration.id) return

      if (data.status === "connected") {
        submitting.value = false
        toast.add({
          severity: "success",
          summary: "Success",
          detail: "Agent configured successfully",
          life: 4000,
        })

        // Merge Ably payload into the existing registration to preserve
        // fields (publicKeyPem, databaseId, etc.) not included in payload.
        const merged: AgentRegistrationType = props.registration
          ? {
              ...props.registration,
              status: data.status,
              name: data.name ?? props.registration.name,
              hostname: data.hostname ?? props.registration.hostname,
              ipAddress: data.ip_address ?? props.registration.ipAddress,
              agentVersion:
                data.agent_version ?? props.registration.agentVersion,
              lastHeartbeatAt:
                data.last_heartbeat_at ?? props.registration.lastHeartbeatAt,
            }
          : ({
              id: regId,
              status: data.status,
              name: data.name ?? null,
              hostname: data.hostname ?? null,
              ipAddress: data.ip_address ?? null,
              agentVersion: data.agent_version ?? null,
              lastHeartbeatAt: data.last_heartbeat_at ?? null,
            } as AgentRegistrationType)

        emit("configured", merged)
      } else if (data.status === "disconnected") {
        submitting.value = false
        toast.add({
          severity: "error",
          summary: "Configuration Failed",
          detail: "Agent configuration failed. Please try again.",
          life: 4000,
        })
      }
    },
  )
}

const stopSubscription = () => {
  if (subscription) {
    subscription.close()
    subscription = null
  }
}

// ─── Config Fetch (for reconfigure) ─────────────────────────────

let configPollInterval: ReturnType<typeof setInterval> | null = null

const stopConfigPoll = () => {
  if (configPollInterval) {
    clearInterval(configPollInterval)
    configPollInterval = null
  }
}

const fetchCurrentConfig = async () => {
  if (!props.registration) return

  fetchingConfig.value = true

  try {
    const result = await apolloClient.mutate({
      mutation: DISPATCH_GET_CURRENT_CONFIG,
      variables: { agentRegistrationId: props.registration.id },
    })
    const data = result.data?.dispatchGetCurrentConfig
    if (data?.errors?.length) {
      fetchingConfig.value = false
      return
    }
    const workItemId = data?.workItem?.id
    if (!workItemId) {
      fetchingConfig.value = false
      return
    }

    stopConfigPoll()
    configPollInterval = setInterval(
      () =>
        void (async () => {
          try {
            const pollResult = await apolloClient.query({
              query: POLL_WORK_ITEM,
              variables: { id: workItemId },
              fetchPolicy: "network-only",
            })
            const item = pollResult.data?.agentWorkItem
            if (!item) return

            if (item.status === "completed") {
              stopConfigPoll()
              fetchingConfig.value = false
              serverUrl.value = item.result?.server_url ?? ""
              pendingProfile.value = item.result?.profile ?? ""
              if (serverUrl.value) void fetchProfiles()
            } else if (item.status === "failed") {
              stopConfigPoll()
              fetchingConfig.value = false
            }
          } catch (err) {
            console.error("Config poll error:", err)
          }
        })(),
      2000,
    )
  } catch (err) {
    console.error("Failed to fetch current config:", err)
    fetchingConfig.value = false
  }
}

// ─── Profile Fetching ───────────────────────────────────────────

let profilePollInterval: ReturnType<typeof setInterval> | null = null

const stopProfilePoll = () => {
  if (profilePollInterval) {
    clearInterval(profilePollInterval)
    profilePollInterval = null
  }
}

const pollProfileWorkItem = (workItemId: string) => {
  stopProfilePoll()
  profilePollInterval = setInterval(
    () =>
      void (async () => {
        try {
          const result = await apolloClient.query({
            query: POLL_WORK_ITEM,
            variables: { id: workItemId },
            fetchPolicy: "network-only",
          })
          const item = result.data?.agentWorkItem
          if (!item) return

          if (item.status === "completed") {
            stopProfilePoll()
            profilesLoading.value = false
            const profileList = item.result?.profiles
            if (Array.isArray(profileList)) {
              profiles.value = [...profileList].sort((a: string, b: string) =>
                a.localeCompare(b),
              )
              if (
                pendingProfile.value &&
                !selectedProfile.value &&
                profiles.value.includes(pendingProfile.value)
              ) {
                selectedProfile.value = pendingProfile.value
              }
            } else {
              profileError.value = "No profiles returned from agent"
            }
          } else if (item.status === "failed") {
            stopProfilePoll()
            profilesLoading.value = false
            profileError.value =
              item.errorMessage || "Failed to fetch profiles from agent"
          }
        } catch (err) {
          console.error("Profile poll error:", err)
        }
      })(),
    2000,
  )
}

const fetchProfiles = async () => {
  if (!serverUrl.value.trim() || !props.registration) return

  profilesLoading.value = true
  profileError.value = ""
  profiles.value = []
  selectedProfile.value = ""

  try {
    const result = await apolloClient.mutate({
      mutation: DISPATCH_LIST_PROFILES,
      variables: {
        agentRegistrationId: props.registration.id,
        serverUrl: serverUrl.value.trim(),
      },
    })
    const data = result.data?.dispatchListProfiles
    if (data?.errors?.length) {
      profilesLoading.value = false
      profileError.value = data.errors.join(", ")
      return
    }
    const workItemId = data?.workItem?.id
    if (workItemId) {
      pollProfileWorkItem(workItemId)
    } else {
      profilesLoading.value = false
      profileError.value = "Failed to dispatch profile request"
    }
  } catch {
    profilesLoading.value = false
    profileError.value = "Failed to dispatch profile request"
  }
}

// ─── Submit Configuration ───────────────────────────────────────

const submitConfiguration = async () => {
  if (!props.registration?.publicKeyPem) {
    toast.add({
      severity: "error",
      summary: "Error",
      detail: "Agent public key not available",
      life: 4000,
    })
    return
  }

  submitting.value = true

  try {
    const credentialData: Record<string, string> = {
      server_url: serverUrl.value.trim(),
      profile: selectedProfile.value,
    }
    if (testOrderNumber.value.trim()) {
      credentialData.test_order_number = testOrderNumber.value.trim()
    }
    const payload = JSON.stringify(credentialData)
    const encrypted = await encryptCredentials(
      props.registration.publicKeyPem,
      payload,
    )

    const result = await apolloClient.mutate({
      mutation: SUBMIT_ENCRYPTED_CREDENTIALS,
      variables: {
        agentRegistrationId: props.registration.id,
        encryptedCredentials: encrypted,
        name: agentName.value.trim() || null,
      },
    })
    const data = result.data?.submitEncryptedCredentials
    if (data?.errors?.length) {
      submitting.value = false
      toast.add({
        severity: "error",
        summary: "Error",
        detail: data.errors.join(", "),
        life: 4000,
      })
    }
    // Success handled via WebSocket (status → connected)
  } catch (err) {
    submitting.value = false
    console.error("Submit credentials error:", err)
    toast.add({
      severity: "error",
      summary: "Error",
      detail:
        err instanceof Error ? err.message : "Failed to submit credentials",
      life: 4000,
    })
  }
}

// ─── Cancel ─────────────────────────────────────────────────────

const handleCancel = async () => {
  if (props.registration) {
    try {
      await apolloClient.mutate({
        mutation: REVOKE_AGENT,
        variables: { agentRegistrationId: props.registration.id },
      })
    } catch {
      toast.add({
        severity: "error",
        summary: "Error",
        detail: "Failed to cancel setup",
        life: 4000,
      })
    }
  }
  emit("cancel")
}

// ─── Lifecycle ──────────────────────────────────────────────────

watch(visible, (isVisible) => {
  if (isVisible) {
    resetForm()
    void startSubscription()
    if (props.initialServerUrl) {
      serverUrl.value = props.initialServerUrl
      pendingProfile.value = props.initialProfile ?? ""
      void fetchProfiles()
    } else if (
      props.registration?.status === "connected" ||
      props.registration?.status === "disconnected"
    ) {
      // Reconfigure flow: fetch current config from agent
      void fetchCurrentConfig()
    }
  } else {
    stopSubscription()
    stopConfigPoll()
    stopProfilePoll()
  }
})

onUnmounted(() => {
  stopSubscription()
  stopConfigPoll()
  stopProfilePoll()
})
</script>
